import { useCallback, useState } from "react";

import type { SplitNodeV2Data } from "src/constants/NodeDataTypes";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { NodeSuggestionType } from "src/copilot/NodeSuggestion";
import { syncEdgesAndNodesForSplitNodeSuggestion } from "src/nodeEditor/splitNodeSuggestionUtils";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";

type BranchToDeleteInfo = {
  branchName: string;
  branchCount: number;
  branchNodeIds: string[];
  nodesToDelete: BeMappedNode[];
};

export type DeletionInfo = {
  type: "split-node-branch";
  data: BranchToDeleteInfo;
};

export const useSplitNodeSuggestion = () => {
  const graphStore = useGraphStore();

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<DeletionInfo | null>(null);
  const [deletionModalResolver, setDeletionModalResolver] = useState<
    ((confirmed: boolean) => void) | null
  >(null);

  const computeSplitNodeUpdates = useCallback(
    (
      nodeId: string,
      suggestion: NodeSuggestionType,
    ): ReturnType<typeof syncEdgesAndNodesForSplitNodeSuggestion> => {
      try {
        const suggestionMeta = suggestion.meta as SplitNodeV2Data;
        return syncEdgesAndNodesForSplitNodeSuggestion(
          graphStore,
          nodeId,
          suggestionMeta,
        );
      } catch (error) {
        logger.error("Failed to compute split node updates", error);
        return undefined;
      }
    },
    [graphStore],
  );

  const showDeletionModal = useCallback(
    (
      updates: NonNullable<
        ReturnType<typeof syncEdgesAndNodesForSplitNodeSuggestion>
      >,
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (updates.branchToDeleteInfo) {
          setDeletionInfo({
            type: "split-node-branch",
            data: updates.branchToDeleteInfo,
          });
        }
        setIsDeletionModalOpen(true);
        setDeletionModalResolver(() => resolve);
      });
    },
    [],
  );

  const clearDeletionModal = useCallback(() => {
    setDeletionInfo(null);
    setIsDeletionModalOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deletionModalResolver) {
      deletionModalResolver(true);
      setDeletionModalResolver(null);
    }
    clearDeletionModal();
  }, [deletionModalResolver, clearDeletionModal]);

  const handleCancelDelete = useCallback(() => {
    if (deletionModalResolver) {
      deletionModalResolver(false);
      setDeletionModalResolver(null);
    }
    clearDeletionModal();
  }, [deletionModalResolver, clearDeletionModal]);

  return {
    computeSplitNodeUpdates,
    showDeletionModal,
    isDeletionModalOpen,
    deletionInfo,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
